import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faLocationDot,
  faCalendarAlt,
  faUsers,
  faHeart,
  faTicket,
  faArrowLeft,
  faChevronLeft,
  faChevronRight,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled Components
const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${(props) => props.theme.colors.background};
`;

const EventHero = styled.div`
  position: relative;
  height: 80vh;
  width: 100%;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)),
      url(${(props) => props.image}) center/cover no-repeat;
    transition: transform 0.3s ease;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 150px;
  background: linear-gradient(
    to top,
    ${(props) => props.theme.colors.background},
    transparent
  );
  z-index: 1;
`;

const BackButton = styled.button`
  position: absolute;
  top: 120px;
  left: 2rem;
  background: transparent;
  border: 2px solid white;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  color: white;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  z-index: 2;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateX(-5px);
  }

  @media (max-width: 768px) {
    top: 330px;
    left: 1rem;
    padding: 0.4rem 0.8rem;
    font-size: 0.9rem;
  }
`;

const HeroContent = styled.div`
  position: absolute;
  bottom: 80px;
  left: 0;
  right: 0;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  color: white;
  animation: ${fadeIn} 1s ease-out;

  @media (max-width: 768px) {
    bottom: 80px;
    padding: 0 1rem;
  }
`;

const EventTitle = styled.h1`
  font-size: clamp(1.8rem, 4vw, 3.5rem);
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1.2;
`;

const EventMeta = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 0.8rem;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.6rem 1rem;
  border-radius: 50px;
  backdrop-filter: blur(5px);

  svg {
    color: ${(props) => props.theme.colors.secondary};
  }

  span {
    font-size: 1rem;
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0.8rem;
    font-size: 0.9rem;
  }
`;

const MainContent = styled.main`
  position: relative;
  z-index: 10;
  margin-top: -4rem;
  padding-bottom: 4rem;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  animation: ${fadeIn} 1s ease-out;
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #eee;
`;

const Tab = styled.button`
  flex: 1;
  padding: 1.5rem 2rem;
  background: ${(props) => (props.active ? "white" : "#f9f9f9")};
  border: none;
  border-bottom: 3px solid
    ${(props) => (props.active ? props.theme.colors.primary : "transparent")};
  font-weight: ${(props) => (props.active ? "700" : "500")};
  color: ${(props) => (props.active ? props.theme.colors.text : "#777")};
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
  white-space: nowrap;
  text-align: center;

  &:hover {
    color: ${(props) => props.theme.colors.primary};
  }

  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 0.9rem;
    min-width: 100px;
  }
`;

const TabContent = styled.div`
  padding: 2.5rem;
  width: 100%; /* Ensure the content takes full width */
  box-sizing: border-box; /* Include padding in the element's total width and height */

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const ContentText = styled.div`
  p {
    margin-bottom: 1.5rem;
    line-height: 1.8;
    color: ${(props) => props.theme.colors.text};
    font-size: 1.05rem;
  }

  strong {
    color: ${(props) => props.theme.colors.primary};
    font-weight: 700;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 2.5rem 0 1.5rem;
  position: relative;
  padding-left: 1rem;
  color: ${(props) => props.theme.colors.text};

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${(props) => props.theme.colors.primary};
    border-radius: 10px;
  }
`;

const AgendaItem = styled.div`
  display: flex;
  gap: 2rem;
  padding: 1.5rem;
  border-radius: 15px;
  background: ${(props) => props.theme.colors.background};
  margin-bottom: 1rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(10px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
`;

const TimeSlot = styled.div`
  min-width: 100px;
  text-align: center;
  padding: 0.5rem;
  background: ${(props) => props.theme.colors.primary};
  color: white;
  border-radius: 8px;
  font-weight: 600;
`;

const AgendaDetails = styled.div`
  flex: 1;

  h4 {
    font-size: 1.2rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: ${(props) => props.theme.colors.text};
  }

  p {
    color: ${(props) => props.theme.colors.textLight};
    line-height: 1.6;
  }
`;

const GalleryContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: ${(props) => props.theme.colors.background};
  margin-bottom: 2rem;
`;

const MainGalleryContainer = styled.div`
  position: relative;
  border-radius: 15px;
  overflow: hidden;
  aspect-ratio: 4/3;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: #000;
  transition: all 0.5s ease;
  display: block;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  padding: 0 2rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const GalleryButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${(props) => (props.direction === "left" ? "left: 1rem;" : "right: 1rem;")}
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.theme.colors.secondary};
    transform: translateY(-50%) scale(1.1);
  }
  
  svg {
    font-size: 1.2rem;
  }
  
  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    ${(props) => (props.direction === "left" ? "left: 0.5rem;" : "right: 0.5rem;")}
  }
`;

const SidebarCard = styled(ContentCard)`
  position: sticky;
  top: 2rem;
  padding: 2rem;
  height: fit-content;

  @media (max-width: 1024px) {
    position: relative;
    top: 0;
    margin-bottom: 2rem;
  }
`;

const ActionButton = styled.button`
  width: 100%;
  background: ${(props) => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 1.2rem;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px ${(props) => props.theme.colors.primary}40;
  }
`;

const HighlightBox = styled.div`
  background: #f9f9f9;
  border-left: 4px solid ${(props) => props.theme.colors.secondary};
  padding: 1.5rem;
  margin: 2rem 0;
  border-radius: 0 10px 10px 0;
`;

const ThumbnailContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 0.5rem;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(70px, 1fr));
  }
`;

const ThumbnailImage = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 3px solid ${props => props.isActive ? props.theme.colors.primary : 'transparent'};
  filter: ${props => props.isActive ? 'brightness(100%)' : 'brightness(70%)'};
  
  &:hover {
    transform: translateY(-3px);
    filter: brightness(100%);
  }
`;

const GalleryHeading = styled(SectionTitle)`
  display: flex;
  align-items: center;
  
  span {
    margin-left: auto;
    font-size: 1rem;
    color: ${props => props.theme.colors.textLight};
    font-weight: 400;
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(0,0,0,0) 60%, rgba(0,0,0,0.7) 100%);
  z-index: 1;
  display: flex;
  align-items: flex-end;
  padding: 1.5rem;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  .caption {
    color: white;
    font-size: 1.1rem;
    font-weight: 500;
  }
  
  &:hover {
    opacity: 1;
  }
`;

const RegistrationInfo = styled.div`
  margin-bottom: 2rem;

  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    color: ${(props) => props.theme.colors.text};
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.border};

  &:last-child {
    border-bottom: none;
  }

  svg {
    color: ${(props) => props.theme.colors.primary};
  }

  div {
    strong {
      display: block;
      margin-bottom: 0.25rem;
      color: ${(props) => props.theme.colors.text};
    }

    p {
      color: ${(props) => props.theme.colors.textLight};
    }
  }
`;

const events = {
  "3-years-impact": {
    id: 1,
    title: "The Big Heart: 3 Years of Impact",
    date: "23th FEB",
    time: "2:00 pm - 8:00 pm",
    location: "Casablanca, Morocco",
    image: "/images/EventPage/Birthday-event.jpg",
    // Originals are unavailable in the repository. Restore from docs/missing-assets.md.
    gallery: [],
    agenda: [
      {
        time: "2:00 PM",
        title: "Opening Ceremony",
        description: "Welcome speech and program overview",
      },
      {
        time: "3:00 PM",
        title: "Impact Showcase",
        description: "Presentation of three years of achievements",
      },
      {
        time: "4:30 PM",
        title: "Community Stories",
        description: "Testimonials from beneficiaries and members",
      },
      {
        time: "6:00 PM",
        title: "Future Vision",
        description: "Unveiling plans for the next chapter",
      },
      {
        time: "7:00 PM",
        title: "Recognition Ceremony",
        description: "Honoring outstanding contributors",
      },
    ],
    highlights: [
      {
        icon: faUsers,
        label: "500+ Expected Attendees",
      },
      {
        icon: faHeart,
        label: "Charity Fundraising",
      },
      {
        icon: faStar,
        label: "Special Performances",
      },
    ],
    description: `On February 23, The Big Heart Association celebrated three years of commitment, solidarity, and impact through the event “The Big Heart: 3 Years of Impact.”
An unforgettable day marked by emotion, gratitude, and the collective pride of a journey walked hand in hand.`,
    fullDescription: `This celebration was an opportunity to honor our most dedicated members — those whose hearts and consistency have carried our mission forward. Trophies and certificates of recognition were awarded to these silent pillars who, day after day, keep the heart of our association beating.

Through the many actions carried out, The Big Heart’s impact has been reflected in looks, smiles, and transformed lives.
This event not only celebrated past achievements, but also opened a new chapter of commitment and hope.

In support of the Heartbeat Journey project, the gathering also took on a spirit of solidarity: raising funds to help achieve the project’s goals.`,
    registration: {
      deadline: "Ended",
      capacity: 70,
      price: "Paid-For Charity Purpose",
      requirements: "Pre-registration required",
    },
  },
  "plastics-forum": {
    id: 2,
    title: "International Plastics Forum and Exhibition",
    date: "26th-28th JUN",
    time: "9:00 am - 5:00 pm",
    location: "Casablanca, Morocco",
    image: "/images/EventPage/event-image.jpg",
    gallery: [
      "/images/EventPage/slide1-event-plastic.jpeg",
      "/images/EventPage/slide2-event-plastic.jpeg",
      "/images/EventPage/slide3-event-plastic.jpeg",
      "/images/EventPage/slide4-event-plastic.jpeg",
    ],
    agenda: [
      {
        time: "9:00 AM",
        title: "Welcome and Registration",
        description: "Check-in and opening remarks",
      },
      {
        time: "10:00 AM",
        title: "Keynote Address",
        description: "The future of plastics and sustainability",
      },
      {
        time: "11:30 AM",
        title: "Panel Discussion",
        description: "Industry experts share insights",
      },
      {
        time: "1:00 PM",
        title: "Networking Lunch",
        description: "Meet and greet with industry professionals",
      },
      {
        time: "2:30 PM",
        title: "Workshop Sessions",
        description: "Hands-on learning and collaboration",
      },
      {
        time: "4:00 PM",
        title: "Closing Ceremony",
        description: "Wrap-up and next steps",
      },
    ],
    highlights: [
      {
        icon: faUsers,
        label: "Industry Professionals",
      },
      {
        icon: faHeart,
        label: "Sustainability Focus",
      },
      {
        icon: faTicket,
        label: "Limited Seating",
      },
    ],
    description: `The Big Heart Association had the honor of being invited to participate in the International Plastics Forum and Exhibition held in Casablanca from June 26 to 28. This three-day event brought together companies, associations, and international stakeholders around the theme of a sustainable future addressing challenges related to plastics management.`,
    fullDescription: `On this occasion, The Big Heart presented its Heartbeat Journey project through a dedicated booth, allowing the team to engage directly with visitors about the project’s vision, actions, and impact. It was a significant moment to highlight our commitment to sustainable education in rural areas, closely aligned with the values of sustainable development.

With high-level conferences, practical workshops, and networking sessions, the event helped strengthen synergies among the various participants and expand our network on both national and international levels.

This participation marked an important milestone for our association, consolidating our positioning on sustainability issues and amplifying the voice of rural education to a broader and engaged audience.`,
    registration: {
      deadline: "Ended",
      capacity: 200,
      price: "Free",
      requirements: "Limited seating available",
    },
  },
  "portugal-event": {
    id: 3,
    title: "The Big Heart Goes Global: Next Stop, Portugal!",
    date: "06th OCT 2025",
    time: "9:00 am - 5:00 pm",
    location: "Lisbon, Portugal",
    image: `${process.env.PUBLIC_URL}/images/sliders/imgi_309_PSGE_3.webp`,
    agenda: [
      {
        time: "8:00 AM - 9:00 AM",
        title: "Registration",
        description: "Check-in and welcome",
      },
      {
        time: "9:00 AM - 9:15 AM",
        title: "Opening Ceremony",
        description: "Amaro Reis - President of APIP | Nacho Dean - Professional Explorer, Speaker and Author | Armindo Monteiro - President of CIP - Confederation of Portuguese Business",
      },
      {
        time: "9:15 AM - 10:45 AM",
        title: "1st Panel",
        description: "Resilient Industry and Integrated Management",
      },
      {
        time: "10:45 AM - 11:15 AM",
        title: "Coffee Break",
        description: "Networking and refreshments",
      },
      {
        time: "11:15 AM - 12:45 PM",
        title: "2nd Panel",
        description: "Transition pathways: A Societal Approach",
      },
      {
        time: "12:45 PM - 2:00 PM",
        title: "Lunch",
        description: "Networking lunch break",
      },
      {
        time: "2:00 PM - 3:30 PM",
        title: "3rd Panel",
        description: "Keep Looping: Enhancing Circularity",
      },
      {
        time: "3:30 PM - 4:00 PM",
        title: "Coffee Break",
        description: "Afternoon networking and refreshments",
      },
      {
        time: "4:00 PM - 5:30 PM",
        title: "4th Panel",
        description: "Regenerative Ecosystem Architecture: From cradle to cradle",
      },
      {
        time: "5:30 PM - 6:00 PM",
        title: "Closing Session",
        description: "Marta Moreira Marques - Expert Committee Coordinator | Romaissae Eddibi - Youth Ambassador | Maria da Graça Carvalho - Minister of Environment and Energy | Government of Portugal",
      },
      {
        time: "6:15 PM",
        title: "Networking Cocktail",
        description: "Closing reception and networking",
      },
    ],
    highlights: [
      {
        icon: faUsers,
        label: "Industry Professionals",
      },
      {
        icon: faHeart,
        label: "Sustainability Focus",
      },
      {
        icon: faTicket,
        label: "Limited Seating",
      },
    ],
    description: `From the shores of Morocco to the heart of Lisbon — The Big Heart Association is taking its mission beyond borders!`,
    fullDescription: `We are thrilled to share that we’ve been invited as an Institutional Partner at the Plastics Summit – Global Event , happening on October 6th, 2025, in Lisbon, Portugal

This prestigious international gathering will unite changemakers, innovators, and environmental leaders to reimagine the future of plastics and sustainability. And this time, The Big Heart will be right there — raising the voice of Moroccan civil society on the global stage.

Through this journey, we’re not just attending a summit — we’re building bridges of impact, sharing stories of hope, and connecting hearts across continents.

Lisbon is calling, and we’re answering — with purpose, passion, and the power of community.

🌟 Let’s take Morocco’s heartbeat to the world. Next stop: Portugal!`,
    registration: {
      deadline: "Ended",
      capacity: "over 1,500",
      price: "Please check the event website for details: plastics summit globalevent",
      requirements: "Limited seating available",
      link: "https://plasticssummit-globalevent.com",
    },
    gallery:[
      `${process.env.PUBLIC_URL}/images/EventPage/portugal-event/portugal-event1.webp`,
      `${process.env.PUBLIC_URL}/images/EventPage/portugal-event/portugal-event2.webp`,
      `${process.env.PUBLIC_URL}/images/EventPage/portugal-event/portugal-event3.webp`,
      `${process.env.PUBLIC_URL}/images/EventPage/portugal-event/portugal-event4.webp`,
      `${process.env.PUBLIC_URL}/images/EventPage/portugal-event/portugal-event5.webp`,
      `${process.env.PUBLIC_URL}/images/EventPage/portugal-event/portugal-event6.webp`,
      `${process.env.PUBLIC_URL}/images/EventPage/portugal-event/portugal-event7.webp`,
      `${process.env.PUBLIC_URL}/images/EventPage/portugal-event/portugal-event8.webp`,
      `${process.env.PUBLIC_URL}/images/EventPage/portugal-event/portugal-event9.webp`,
      `${process.env.PUBLIC_URL}/images/EventPage/portugal-event/portugal-event10.webp`,
      `${process.env.PUBLIC_URL}/images/EventPage/portugal-event/portugal-event11.webp`,
    ],
  },
  "marche-de-printemps": {
    id: 4,
    title: "The Big Heart au Marché de Printemps",
    date: "14th JUN 2026",
    time: "All day",
    location: "Triangle Vert Bouskoura",
    image: `${process.env.PUBLIC_URL}/images/EventPage/marche-du-printemps/printemps-4.jpeg`,
    description: `Nous sommes heureux d’annoncer notre participation en tant que partenaire au Marché de Printemps, qui se tiendra ce dimanche 14 juin 2026 au Triangle Vert Bouskoura.

À travers ce partenariat, The Big Heart s’inscrit dans une dynamique de partage, de solidarité et d’engagement citoyen. Cet événement familial met à l’honneur la créativité des enfants et des jeunes, tout en créant un véritable espace de rencontre autour de valeurs humaines fortes.`,
    fullDescription: `Lors de cette journée, nous aurons le plaisir de présenter notre projet humanitaire « Heartbeat Journey », d’échanger avec la communauté dans un environnement chaleureux et convivial, et de sensibiliser à nos initiatives en faveur des populations vulnérables.

Parce que chaque geste compte, nous croyons que ces moments de proximité permettent de renforcer les liens et de construire ensemble un avenir plus solidaire.

✨ Nous vous donnons rendez-vous ce dimanche pour partager ce moment avec nous ! Venez nombreux nous rencontrer et soutenir cette belle initiative.`,
    registration: {
      deadline: "Ended",
      capacity: "Open community participation",
      price: "Free",
      requirements: "Family-friendly event",
    },
    gallery: [
      `${process.env.PUBLIC_URL}/images/EventPage/marche-du-printemps/printemps-1.webp`,
      `${process.env.PUBLIC_URL}/images/EventPage/marche-du-printemps/printemps-2.webp`,
      `${process.env.PUBLIC_URL}/images/EventPage/marche-du-printemps/printemps-3.webp`,
      `${process.env.PUBLIC_URL}/images/EventPage/marche-du-printemps/printemps-4.jpeg`,
      `${process.env.PUBLIC_URL}/images/EventPage/marche-du-printemps/printemps-5.jpeg`,
      `${process.env.PUBLIC_URL}/images/EventPage/marche-du-printemps/printemps-6.jpeg`,
      `${process.env.PUBLIC_URL}/images/EventPage/marche-du-printemps/printemps-7.jpeg`,
      `${process.env.PUBLIC_URL}/images/EventPage/marche-du-printemps/printemps-8.jpeg`,

    ],
  },
};

const EventDetailsPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("about");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const event = events[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!event) {
    return (
      <PageWrapper>
        <ContentCard
          style={{ margin: "120px auto", maxWidth: "600px", padding: "2rem" }}
        >
          <h2>Event not found</h2>
          <p>Sorry, the event you are looking for does not exist.</p>
          <ActionButton onClick={() => navigate("/events")}>
            Back to Events
          </ActionButton>
        </ContentCard>
      </PageWrapper>
    );
  }

  const formatEventDescription = (text) => {
    return text.split(" ").map((word, i) => {
      if (
        ["mission", "impact", "hope", "transform", "future", "change"].includes(
          word.toLowerCase().replace(/[.,!]/g, "")
        )
      ) {
        return <strong key={i}>{word} </strong>;
      }
      return word + " ";
    });
  };

  return (
    <PageWrapper>
      <EventHero image={event.image}>
        <BackButton onClick={() => navigate("/events")}>
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Events
        </BackButton>
        <HeroContent>
          <EventTitle>{event.title}</EventTitle>
          <EventMeta>
            <MetaItem>
              <FontAwesomeIcon icon={faCalendarAlt} />
              <span>{event.date}</span>
            </MetaItem>
            <MetaItem>
              <FontAwesomeIcon icon={faClock} />
              <span>{event.time}</span>
            </MetaItem>
            <MetaItem>
              <FontAwesomeIcon icon={faLocationDot} />
              <span>{event.location}</span>
            </MetaItem>
          </EventMeta>
        </HeroContent>
        <HeroOverlay />
      </EventHero>

      <MainContent>
        <ContentContainer>
          <ContentCard>
            <TabsContainer>
              <Tab
                active={activeTab === "about"}
                onClick={() => setActiveTab("about")}
              >
                About
              </Tab>
              {event.agenda && event.agenda.length > 0 && (
                <Tab
                  active={activeTab === "agenda"}
                  onClick={() => setActiveTab("agenda")}
                >
                  Agenda
                </Tab>
              )}
              {Array.isArray(event.gallery) && (
                <Tab
                  active={activeTab === "gallery"}
                  onClick={() => setActiveTab("gallery")}
                >
                  Gallery
                </Tab>
              )}
            </TabsContainer>

            <TabContent>
              {activeTab === "about" && (
                <ContentText>
                  <p>{formatEventDescription(event.description)}</p>
                  <SectionTitle>Event Details</SectionTitle>
                  {event.fullDescription
                    .split("\n\n")
                    .map((paragraph, index) => (
                      <p key={index}>{formatEventDescription(paragraph)}</p>
                    ))}
                  <HighlightBox>
                    <p>
                      <strong>Join us</strong> for this transformative event as
                      we come together to celebrate achievements and create
                      lasting change in our community.
                    </p>
                  </HighlightBox>
                </ContentText>
              )}

              {activeTab === "agenda" && (
                <div>
                  <SectionTitle>Event Schedule</SectionTitle>
                  {event.agenda.map((item, index) => (
                    <AgendaItem key={index}>
                      <TimeSlot>{item.time}</TimeSlot>
                      <AgendaDetails>
                        <h4>{item.title}</h4>
                        <p>{item.description}</p>
                      </AgendaDetails>
                    </AgendaItem>
                  ))}
                </div>
              )}



              {activeTab === "gallery" && (
                <>
                  <GalleryHeading>
                    Event Gallery
                    {!!event.gallery?.length && <span>{currentImageIndex + 1} / {event.gallery.length}</span>}
                  </GalleryHeading>

                  {!event.gallery?.length ? (
                    <ContentText>
                      <p>Photos coming soon.</p>
                    </ContentText>
                  ) : (
                    <GalleryContainer>
                      <MainGalleryContainer>
                        <GalleryImage
                          src={event.gallery[currentImageIndex]}
                          alt={`Event gallery ${currentImageIndex + 1}`}
                        />
                        <ImageOverlay>
                          <div className="caption">The Big Heart Event {currentImageIndex + 1}</div>
                        </ImageOverlay>
                        <GalleryButton
                          direction="left"
                          onClick={() =>
                            setCurrentImageIndex((prev) =>
                              prev === 0 ? event.gallery.length - 1 : prev - 1
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </GalleryButton>
                        <GalleryButton
                          direction="right"
                          onClick={() =>
                            setCurrentImageIndex((prev) =>
                              prev === event.gallery.length - 1 ? 0 : prev + 1
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faChevronRight} />
                        </GalleryButton>
                      </MainGalleryContainer>

                      <ThumbnailContainer>
                        {event.gallery.map((img, index) => (
                          <ThumbnailImage
                            key={index}
                            src={img}
                            alt={`Thumbnail ${index + 1}`}
                            isActive={index === currentImageIndex}
                            onClick={() => setCurrentImageIndex(index)}
                          />
                        ))}
                      </ThumbnailContainer>
                    </GalleryContainer>
                  )}
                </>
              )}
            </TabContent>
          </ContentCard>

          <SidebarCard>
            <RegistrationInfo>
              <h3>Event Details</h3>
              <InfoItem>
                <FontAwesomeIcon icon={faUsers} />
                <div>
                  <strong>Capacity</strong>
                  <p>{event.registration.capacity} attendees</p>
                </div>
              </InfoItem>
              <InfoItem>
                <FontAwesomeIcon icon={faCalendarAlt} />
                <div>
                  <strong>Registration Deadline</strong>
                  <p>{event.registration.deadline}</p>
                </div>
              </InfoItem>
              <InfoItem>
                <FontAwesomeIcon icon={faStar} />
                <div>
                  <strong>Entry</strong>
                  <p>{event.registration.price}</p>
                </div>
              </InfoItem>
            </RegistrationInfo>

            {event.registration.deadline === "Ended" ? (
              <ActionButton disabled>Registration Ended</ActionButton>
            ) : event.registration.link ? (
              <ActionButton as="a" href={event.registration.link} target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faHeart} />
                Register Now
              </ActionButton>
            ) : (
              <ActionButton>
                <FontAwesomeIcon icon={faHeart} />
                Register Now
              </ActionButton>
            )}
          </SidebarCard>
        </ContentContainer>
      </MainContent>
    </PageWrapper>
  );
};

export default EventDetailsPage;
